import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, MapPin, Pencil, Plus, QrCode, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useOutletContext } from "react-router";
import { locationSchema } from "../../../shared/schemas";
import { api } from "../../api/client";
import { Modal } from "../../components/kit";
import { Alert, ICON, TextField } from "../../components/ui";
import { apiFailure, type FieldErrors, validate } from "../../pages/forms";
import type { ShellContext } from "../AppShell";
import { HoursEditor } from "./HoursEditor";

// Setări → Locații (owner 2026-11-04): the same menu served in more places. Each location has its own name, address,
// phone, hours, link and QR code; the menu, prices and appearance are the restaurant's. A further location costs
// 2.90 € / month or 19 € / year + VAT; on a running paid subscription the months left until renewal are charged
// at once, shown before the owner confirms.

interface Loc {
  id: string;
  name: string;
  address: string;
  city: string;
  phone: string;
  hours: string;
  primary: boolean;
  publicUrl: string;
  views30: number;
}
interface Quote {
  /** Shown in euro to everyone; `chargeNow` is in the currency actually charged. */
  monthMinor: number;
  yearMinor: number;
  chargeNow: { currency: "EUR" | "RON"; months: number; amountMinor: number } | null;
}
type Form = { name: string; address: string; city: string; phone: string; hours: string };
const EMPTY: Form = { name: "", address: "", city: "", phone: "", hours: "" };

const money = (minor: number, cur: "EUR" | "RON") =>
  cur === "RON"
    ? `${(minor / 100).toFixed(2).replace(".", ",")} lei`
    : `${(minor / 100).toFixed(2).replace(".", ",")} €`;

export function LocationsTab() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const owner = membership.role === "owner";
  const qc = useQueryClient();
  const key = ["locations", rid];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ locations: Loc[]; quote: Quote }>("GET", `/restaurants/${rid}/locations`),
  });
  const [editing, setEditing] = useState<Loc | "new" | null>(null);
  const [removing, setRemoving] = useState<Loc | null>(null);
  const [message, setMessage] = useState("");

  if (q.isPending) return <p className="muted">Se încarcă locațiile…</p>;
  if (q.isError) return <Alert tone="error">{q.error.message}</Alert>;
  const { locations, quote } = q.data;
  const price = `${money(quote.monthMinor, "EUR")} / lună sau ${money(quote.yearMinor, "EUR")} / an + TVA`;

  async function remove(l: Loc) {
    setMessage("");
    try {
      const r = await api<{ locations: Loc[] }>("DELETE", `/restaurants/${rid}/locations/${l.id}`);
      qc.setQueryData(key, { locations: r.locations, quote });
      setRemoving(null);
    } catch (e) {
      setMessage(apiFailure(e).message);
    }
  }

  return (
    <section className="card stack" aria-labelledby="loc-h" style={{ gap: 16 }}>
      <div className="stack" style={{ gap: 6 }}>
        <h2 id="loc-h" className="h2" style={{ margin: 0 }}>
          Locații
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          Ai același meniu în mai multe locuri? Adaugă o locație: are adresa, telefonul, programul, linkul și
          codul QR ei, iar meniul, prețurile și aspectul rămân comune. Fiecare locație în plus costă {price}.
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      <ul className="loc-list">
        {locations.map((l) => (
          <li key={l.id} className="loc-item">
            <MapPin {...ICON} aria-hidden="true" />
            <div className="loc-text">
              <strong>
                {l.name}
                {l.primary && <span className="caption muted"> · locația principală</span>}
              </strong>
              <span className="caption muted">
                {[l.address, l.city].filter(Boolean).join(", ") || "Fără adresă"}
                {l.phone ? ` · ${l.phone}` : ""}
              </span>
              <span className="caption muted">
                {l.views30 === 1 ? "o deschidere" : `${l.views30} deschideri`} ale meniului în ultimele 30 de
                zile
              </span>
              <span className="loc-actions">
                <a href={l.publicUrl} target="_blank" rel="noopener" className="btn btn-ghost btn-sm">
                  <ExternalLink {...ICON} aria-hidden="true" />
                  Deschide
                </a>
                <Link to={`/app/qr?loc=${l.id}`} className="btn btn-ghost btn-sm">
                  <QrCode {...ICON} aria-hidden="true" />
                  Cod QR
                </Link>
                {owner && !l.primary && (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(l)}>
                      <Pencil {...ICON} aria-hidden="true" />
                      Modifică
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(l)}>
                      <Trash2 {...ICON} aria-hidden="true" />
                      Șterge
                    </button>
                  </>
                )}
                {owner && l.primary && (
                  <span className="caption muted">Datele ei se schimbă din fila Restaurant.</span>
                )}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {owner && (
        <button
          type="button"
          className="btn btn-primary"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setEditing("new")}
        >
          <Plus {...ICON} aria-hidden="true" />
          Adaugă o locație
        </button>
      )}
      {editing && (
        <LocationDialog
          rid={rid}
          loc={editing === "new" ? null : editing}
          quote={quote}
          price={price}
          onClose={() => setEditing(null)}
          onSaved={(list) => {
            qc.setQueryData(key, { locations: list, quote });
            void qc.invalidateQueries({ queryKey: key });
            setEditing(null);
          }}
        />
      )}
      {removing && (
        <Modal
          title={`Ștergi locația „${removing.name}”?`}
          body="Linkul și codul QR ale locației nu vor mai funcționa. Nu se returnează bani; locația iese de pe abonament la următoarea reînnoire."
          onClose={() => setRemoving(null)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setRemoving(null)}>
                Renunță
              </button>
              <button type="button" className="btn btn-danger" onClick={() => void remove(removing)}>
                Șterge locația
              </button>
            </>
          }
        />
      )}
    </section>
  );
}

function LocationDialog({
  rid,
  loc,
  quote,
  price,
  onClose,
  onSaved,
}: {
  rid: string;
  loc: Loc | null;
  quote: Quote;
  price: string;
  onClose: () => void;
  onSaved: (list: Loc[]) => void;
}) {
  const [form, setForm] = useState<Form>(
    loc
      ? { name: loc.name, address: loc.address, city: loc.city, phone: loc.phone, hours: loc.hours }
      : EMPTY,
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const charge = quote.chargeNow;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const v = validate(locationSchema, form);
    setErrors(v.ok ? {} : v.errors);
    if (!v.ok) return;
    setBusy(true);
    setMessage("");
    try {
      const r = loc
        ? await api<{ locations: Loc[] }>("PUT", `/restaurants/${rid}/locations/${loc.id}`, form)
        : await api<{ locations: Loc[] }>("POST", `/restaurants/${rid}/locations`, {
            ...form,
            expectedChargeMinor: charge?.amountMinor ?? null,
          });
      onSaved(r.locations);
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={loc ? `Modifică „${loc.name}”` : "Adaugă o locație"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Renunță
          </button>
          <button type="submit" form="loc-form" className="btn btn-primary" disabled={busy}>
            {busy ? "Se salvează…" : loc ? "Salvează" : charge ? "Plătește și adaugă" : "Adaugă locația"}
          </button>
        </>
      }
    >
      <form id="loc-form" className="stack" style={{ gap: 12 }} onSubmit={submit} noValidate>
        <TextField
          id="loc-name"
          label="Numele locației"
          hint="De exemplu: Centru, Mall, Aeroport"
          value={form.name}
          maxLength={60}
          onChange={(e) => set("name")(e.target.value)}
          error={errors.name}
        />
        <TextField
          id="loc-address"
          label="Adresa"
          value={form.address}
          maxLength={120}
          onChange={(e) => set("address")(e.target.value)}
          error={errors.address}
        />
        <TextField
          id="loc-city"
          label="Orașul"
          value={form.city}
          maxLength={120}
          onChange={(e) => set("city")(e.target.value)}
          error={errors.city}
        />
        <TextField
          id="loc-phone"
          label="Telefon"
          value={form.phone}
          maxLength={40}
          onChange={(e) => set("phone")(e.target.value)}
          error={errors.phone}
        />
        <HoursEditor value={form.hours} onChange={set("hours")} error={errors.hours} />
        {!loc && (
          <Alert tone="info">
            {charge
              ? `Azi se încasează ${money(charge.amountMinor, charge.currency)} + TVA pentru ${charge.months} ${charge.months === 1 ? "lună" : "luni"} până la reînnoirea abonamentului, cu cardul abonamentului. Apoi locația se plătește odată cu abonamentul: ${price}.`
              : `Locația costă ${price}. Se adaugă la abonament; în perioada gratuită nu plătești nimic pentru ea.`}
          </Alert>
        )}
        {message && (
          <Alert tone="error" live>
            {message}
          </Alert>
        )}
      </form>
    </Modal>
  );
}
